/**
 * aurora.js — northern-lights ribbon for the snow / winter night sky.
 *
 * A single curved curtain (a partial open cylinder) hung high in the north of the
 * star dome, lit by a procedural shader: drifting vertical curtains of green→violet
 * light that shimmer over time. It reuses the night-sky recipe shared by
 * starfield.js / shootingStars.js — additive blending, fog disabled, depthWrite off,
 * drawn as background — so the far dome isn't washed out by the garden's FogExp2.
 *
 * Built once at boot and added to the scene by main.js's initSky(); updateAurora()
 * is called each frame from animate(). It only shows when BOTH conditions hold:
 *   • night  — driven by the same sun-height factor that fades the starfield in, and
 *   • winter — parameters.leafType === 'none' (the Bare / Winter scene, same gate as
 *     updateWinter() in snow.js).
 * Both fades are eased so the ribbon glides in/out rather than popping.
 */

import * as THREE from 'three';

const AURORA = {
  radius:         1250,  // horizontal distance of the curtain from the origin
  height:         750,   // vertical extent of the curtain
  yCenter:        560,   // world-y of the curtain's middle (sits high in the north sky)
  radialSegments: 96,    // around the arc — enough u-resolution for smooth streaks
  heightSegments: 8,     // for the vertex undulation
  // Centered on -Z (θ=π), straight ahead of the default camera at +Z; wide arc so
  // it stays in view as the camera orbits.
  thetaStart:     Math.PI * 0.3,
  thetaLength:    Math.PI * 1.4,
  maxOpacity:     0.28,  // cap the additive bloom (keep it subtle and wispy)
  fadeRate:       1.6,   // winter-presence ease speed (≈0.6s in/out)
};

// Aurora palette: green-teal low, fading up through cyan to a violet crown.
const COLOR_LOW  = new THREE.Color(0x33ff99).multiplyScalar(1.1);
const COLOR_HIGH = new THREE.Color(0x7a4bff).multiplyScalar(1.1);

const vertexShader = /* glsl */`
  varying vec2 vUv;
  uniform float uTime;
  void main() {
    vUv = uv;
    vec3 p = position;
    // Big, slow, low-frequency bends in/out along the radial direction so the band
    // genuinely snakes — overlapping a few octaves keeps the curve organic, not sinusoidal.
    vec3 radial = normalize(vec3(position.x, 0.0, position.z));
    float meander = sin(uv.x * 1.3 + uTime * 0.10) * 1.0
                  + sin(uv.x * 2.1 - uTime * 0.16) * 0.6
                  + sin(uv.x * 3.7 + uTime * 0.22) * 0.3;
    p.xz += radial.xz * meander * 150.0;
    // The band also rises and dips along its length (the defining aurora "fold"),
    // with a finer ripple riding on top.
    p.y += sin(uv.x * 1.7 + uTime * 0.13) * 70.0
         + sin(uv.x * 3.1 - uTime * 0.09) * 34.0
         + sin(uv.x * 9.0 + uTime * 0.45) * 9.0;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  }
`;

const fragmentShader = /* glsl */`
  varying vec2 vUv;
  uniform float uTime;
  uniform float uOpacity;
  uniform vec3  uColorLow;
  uniform vec3  uColorHigh;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  void main() {
    float x = vUv.x;
    float y = vUv.y;

    // Broad, slowly drifting curtains: overlap two octaves so bright bands wander.
    float n = noise(vec2(x * 7.0,  uTime * 0.14));
    n += 0.5 * noise(vec2(x * 15.0 + 3.0, uTime * 0.21));
    n /= 1.5;
    // Broader, softer bright bands (lower contrast) so it reads as a hazy ribbon.
    float curtain = smoothstep(0.30, 0.98, n);

    // Vertical profile: soft bottom edge, brightest low, gently fading to the crown.
    float vert = smoothstep(0.0, 0.28, y) * (1.0 - smoothstep(0.45, 1.0, y));

    // Fine vertical rays — subtle flicker, not strong striping.
    float rays = 0.75 + 0.25 * sin(x * 130.0 + uTime * 2.0 + n * 9.0);

    // Soft fade at both ends of the arc so it dissolves into the sky instead of
    // stopping at a hard edge.
    float ends = smoothstep(0.0, 0.16, x) * (1.0 - smoothstep(0.84, 1.0, x));

    float intensity = curtain * vert * rays * ends;
    vec3 col = mix(uColorLow, uColorHigh, y);

    // Additive blending adds rgb * alpha, so fold intensity into alpha and let
    // uOpacity handle the night/winter fade.
    gl_FragColor = vec4(col, intensity * uOpacity);
  }
`;

let mesh = null;
let presence = 0; // eased winter state, 0 (no aurora) → 1 (winter scene)

/**
 * Build the aurora curtain. Call once; add the returned mesh to the scene.
 * @returns {THREE.Mesh}
 */
export function createAurora() {
  const geometry = new THREE.CylinderGeometry(
    AURORA.radius, AURORA.radius, AURORA.height,
    AURORA.radialSegments, AURORA.heightSegments,
    true,                       // open-ended — it's a curtain, not a tube
    AURORA.thetaStart, AURORA.thetaLength
  );
  geometry.translate(0, AURORA.yCenter, 0);

  const material = new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uTime:      { value: 0 },
      uOpacity:   { value: 0 },
      uColorLow:  { value: COLOR_LOW },
      uColorHigh: { value: COLOR_HIGH },
    },
    transparent: true,
    blending:    THREE.AdditiveBlending,
    side:        THREE.DoubleSide, // viewed from inside the dome
    depthWrite:  false,
    depthTest:   true,
    fog:         false,            // lives on the far dome, beyond the garden's FogExp2
  });

  mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;      // big curtain; cheaper than per-frame bounds checks
  mesh.renderOrder = -1;           // background, like the starfield
  mesh.visible = false;
  return mesh;
}

/**
 * Drive the aurora each frame from animate().
 * @param {number} timeSeconds - performance.now()/1000 (shimmer clock)
 * @param {number} dt          - frame delta in seconds (for the winter ease)
 * @param {number} nightVis    - 0 (day) → 1 (full night); same factor that fades the stars
 * @param {boolean} winter     - parameters.leafType === 'none'
 */
export function updateAurora(timeSeconds, dt, nightVis, winter) {
  if (!mesh) return;
  presence += ((winter ? 1 : 0) - presence) * Math.min(1, dt * AURORA.fadeRate);

  const opacity = nightVis * presence * AURORA.maxOpacity;
  mesh.material.uniforms.uTime.value = timeSeconds;
  mesh.material.uniforms.uOpacity.value = opacity;
  mesh.visible = opacity > 0.002;
}
